import React, { useState, useRef, useEffect } from 'react';
import { Send, Mail, Building, User, MessageSquare, Phone, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { saveContactFormData } from '@/data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    userType: 'buyer',
    productInterest: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const productCategories = [
    'Textiles',
    'Handicrafts',
    'Home Décor',
    'Agricultural Products',
    'Papers / Stationeries',
    'Custom Sourcing'
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleProductInterest = (product) => {
    setFormData(prev => ({
      ...prev,
      productInterest: prev.productInterest.includes(product)
        ? prev.productInterest.filter(p => p !== product)
        : [...prev.productInterest, product]
    }));
  };

  const removeProductInterest = (product) => {
    setFormData(prev => ({
      ...prev,
      productInterest: prev.productInterest.filter(p => p !== product)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      setIsSubmitting(false);
      return;
    }

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Save to localStorage (can be upgraded to backend later)
    const result = saveContactFormData(formData);

    if (result.success) {
      toast.success('Thank you! Your enquiry has been submitted. We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        userType: 'buyer',
        productInterest: [],
        message: ''
      });
    } else {
      toast.error('Something went wrong. Please try again.');
    }

    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-accent/20 relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-foreground/5 via-transparent to-amber-500/5 dark:to-amber-500/10 pointer-events-none" />

      <div className="container relative mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            Trade Enquiries Welcome
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Connect with us to explore partnership opportunities. We welcome enquiries from importers, distributors, sourcing partners, and retailers.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-background/50 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-10 shadow-xl space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Your full name"
                value={formData.name}
                onChange={handleChange}
                required
                className="transition-all focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
              />
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your.email@company.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="transition-all focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
              />
            </div>

            {/* Phone Number Field */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone Number
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+91 98765 43210"
                value={formData.phone}
                onChange={handleChange}
                className="transition-all focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
              />
            </div>

            {/* Company Field */}
            <div className="space-y-2">
              <Label htmlFor="company" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Company
              </Label>
              <Input
                id="company"
                name="company"
                type="text"
                placeholder="Your company name"
                value={formData.company}
                onChange={handleChange}
                className="transition-all focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
              />
            </div>

            {/* User Type Field */}
            <div className="space-y-2">
              <Label htmlFor="userType" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                I am a <span className="text-red-500">*</span>
              </Label>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
              >
                <option value="buyer">Buyer / Importer</option>
                <option value="supplier">Supplier / Manufacturer</option>
                <option value="distributor">Distributor</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Product Interest Multi-Select Field */}
            <div className="space-y-2" ref={dropdownRef}>
              <Label htmlFor="productInterest" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                Product Interest / Category <span className="text-red-500">*</span>
              </Label>
              
              {/* Selected Products as Badges */}
              {formData.productInterest.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.productInterest.map((product) => (
                    <span
                      key={product}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-foreground/10 dark:bg-amber-600/20 text-foreground border border-border/50 hover:border-border transition-colors"
                    >
                      {product}
                      <button
                        type="button"
                        onClick={() => removeProductInterest(product)}
                        className="ml-1 hover:bg-foreground/20 rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${product}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Dropdown Toggle */}
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all hover:bg-accent/50"
              >
                <span className="text-muted-foreground">
                  {formData.productInterest.length === 0 
                    ? 'Select product categories...' 
                    : `${formData.productInterest.length} selected`}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full max-w-md rounded-md border border-border bg-background shadow-lg animate-in fade-in slide-in-from-top-2">
                  <div className="p-2 space-y-1">
                    {productCategories.map((product) => (
                      <label
                        key={product}
                        className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={formData.productInterest.includes(product)}
                          onChange={() => toggleProductInterest(product)}
                          className="h-4 w-4 rounded border-border text-foreground focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
                        />
                        <span className="text-sm">{product}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Message Field */}
            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Message <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="message"
                name="message"
                placeholder="Tell us about your requirements, product interests, or any questions you have..."
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="resize-none transition-all focus:ring-2 focus:ring-foreground/20 dark:focus:ring-amber-500/20"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full group bg-foreground text-background hover:bg-foreground/90 dark:bg-amber-600 dark:hover:bg-amber-700"
              size="lg"
            >
              {isSubmitting ? (
                'Submitting...'
              ) : (
                <>
                  Send Enquiry
                  <Send className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center pt-2">
              * Required fields. Your information will be kept confidential.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
